import React, { useCallback, useEffect, useRef, useState } from 'react'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { generateRequestId } from '../utils/requestId.js'
import { buildSavePayload, mergeSavedRowIds } from '../discounting/autosave.js'
import {
  DEFAULT_DISCOUNTING_METHOD,
  FIXED_AMOUNT,
  PERCENT_PURCHASE_PRICE,
  blankDraftRow,
  nextLocalRowKey,
  normalizeProps,
  withSeededRow
} from '../discounting/formState.js'
import {
  adjustByBasisPoints,
  formatDecimalInput,
  formatFixedAmountInput,
  isValidDecimalDraft,
  isValidFixedAmountDraft,
  parseDecimalInput,
  parseFixedAmountInput
} from '../utils/decimalInput.js'

const DISCOUNTING_METHODS = [
  'MONTHLY_IN_ADVANCE', 'MONTHLY_IN_ARREARS', 'QUARTERLY', 'ANNUALLY'
]
const INPUT_METHODS = [PERCENT_PURCHASE_PRICE, FIXED_AMOUNT]

// Order is part of the spec: -25, -5, +5, +25, left to right.
const BASIS_POINT_STEPS = [
  { key: 'minus_25', bps: -25 },
  { key: 'minus_5', bps: -5 },
  { key: 'plus_5', bps: 5 },
  { key: 'plus_25', bps: 25 }
]

// EVA-283: the same debounce the Handsontable tabs use (OperatingExpensesTab) —
// long enough that a typed rate is one request, short enough to feel immediate.
const DEBOUNCE_DELAY = 800

// Caps the set of request ids the cable handler recognises as its own. Far above
// the number of saves that can overlap; it exists only so a broadcast that never
// arrives cannot leak one entry per save.
const MAX_TRACKED_REQUEST_IDS = 20

function formatAmount(value, inputMethod) {
  return inputMethod === FIXED_AMOUNT ? formatFixedAmountInput(value) : formatDecimalInput(value)
}

// normalizeProps owns the camelCase/snake_case question (ERB props are
// camelized by the mount controller, fetch and cable payloads are not); this
// only turns the normalized shape into editable draft strings.
function toFormState(source) {
  const normalized = normalizeProps(source)
  return {
    discountRatePercent: formatDecimalInput(normalized.discountRatePercent),
    discountingMethod: normalized.discountingMethod || DEFAULT_DISCOUNTING_METHOD,
    vatRatePercent: formatDecimalInput(normalized.vatRatePercent),
    rows: normalized.rows.map((row) => ({
      key: row.id ? `row-${row.id}` : nextLocalRowKey(),
      id: row.id,
      name: row.name,
      inputMethod: row.inputMethod,
      amount: row.amount === null ? '' : formatAmount(row.amount, row.inputMethod),
      applyVat: row.applyVat
    }))
  }
}

export default function DiscountingTab(props) {
  const {
    valuationId, updateUrl, stateUrl,
    csrfToken, readOnly = false, translations
  } = props

  const [form, setForm] = useState(() => withSeededRow(toFormState(props), { readOnly }))
  // { code, message } rather than a bare string: `touch` needs to tell a
  // row-scoped validation error (which its own edit may have invalidated) from a
  // terminal one whose message is the only recovery instruction the user gets.
  const [saveError, setSaveError] = useState(null)
  // Per-row validation errors, keyed by the local row key. The contract reports
  // them by submitted index, which is translated on arrival — see flushSave.
  const [rowErrors, setRowErrors] = useState({})

  // EVA-283: the form is mirrored in a ref because the debounced flush and the
  // unmount cleanup both read it from outside the render cycle. An effect that
  // synced the ref after commit would let a flush fire against the previous
  // render's form, so every write goes through setFormState and moves both.
  //
  // The rule for reading, since getting it wrong here loses data: rendering and
  // event handlers read `form`, which React has committed by the time they run;
  // anything that runs outside the render cycle — a timer, a cleanup, an unload
  // handler, the continuation after an await — reads `formRef.current`.
  const formRef = useRef(form)
  const dirtyRef = useRef(false)
  const inFlightRef = useRef(false)
  const rerunRef = useRef(false)
  const editSeqRef = useRef(0)
  const flushTimeoutRef = useRef(null)
  const flushRef = useRef(null)
  const ownRequestIdsRef = useRef(new Set())

  const setFormState = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(formRef.current) : updater
    formRef.current = next
    setForm(next)
  }, [])

  const errorMessage = useCallback((code) => {
    // t() returns the lookup path itself on a miss (never falsy), so detect an
    // unmapped error code by comparing against the path and fall back.
    const key = `errors.${code}`
    const message = t(key, translations)
    return message === key ? t('errors.save_failed', translations) : message
  }, [translations])

  // EVA-283: props are the first paint, the server is the truth. A Turbo
  // restoration visit re-mounts this component from a cached snapshot whose
  // props are as old as the first render of that page — everything autosave has
  // written since is missing from them, and with no Save button the next
  // keystroke would carry that stale form back to the server. The dirty guard is
  // the same one the cable handler uses: never overwrite an unsaved edit.
  useEffect(() => {
    if (!stateUrl) return undefined

    fetch(stateUrl, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((body) => {
        if (dirtyRef.current || !body?.data) return

        setFormState((prev) => withSeededRow(
          toFormState(body.data), { readOnly, previousRows: prev.rows }
        ))
      })
      .catch(() => {})

    return undefined
  }, [stateUrl, readOnly, setFormState])

  // Registers the id so the cable handler can recognise the echo, then sends.
  // `keepalive` is what lets the unload path outlive the document.
  const sendSave = useCallback((current, requestId, keepalive = false) => {
    // FIFO, not clear(): evicting the oldest cannot drop the id of a request
    // still in the air, which is the one thing the set exists to remember.
    if (ownRequestIdsRef.current.size >= MAX_TRACKED_REQUEST_IDS) {
      ownRequestIdsRef.current.delete(ownRequestIdsRef.current.values().next().value)
    }
    ownRequestIdsRef.current.add(requestId)

    // One request for the whole tab: the assumption and the schedule share a
    // transaction server-side, so a rejected row can never leave the rate saved
    // on its own.
    return fetch(updateUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(buildSavePayload(current, requestId)),
      keepalive
    })
  }, [updateUrl, csrfToken])

  // EVA-283: there is no Save button — every edit schedules this. It reads the
  // form from the ref rather than from state so its identity stays stable, which
  // is what keeps the unmount cleanup below firing only on unmount.
  const flushSave = useCallback(async () => {
    if (readOnly || !dirtyRef.current) return
    // A flush requested during a request is queued, never dropped. Dropping it
    // is what a plain `saving` guard would do, and it would silently lose the
    // keystrokes that asked for it.
    if (inFlightRef.current) {
      rerunRef.current = true
      return
    }

    inFlightRef.current = true
    setSaveError(null)

    const seqAtStart = editSeqRef.current
    const current = formRef.current
    const submittedKeys = current.rows.map((row) => row.key)
    const requestId = generateRequestId()

    try {
      const response = await sendSave(current, requestId)
      const body = await response.json()
      if (!body.success) {
        setSaveError({ code: body.error, message: errorMessage(body.error) })
        // The server answers in submitted positions; local state has moved on.
        // Translate through the same keys the id merge uses, or a row removed
        // during the flight shifts every highlight onto the wrong row.
        const errorsByKey = {}
        Object.entries(body.errors_by_index || {}).forEach(([index, rowError]) => {
          const key = submittedKeys[Number(index)]
          if (key) errorsByKey[key] = rowError
        })
        setRowErrors(errorsByKey)
        return
      }

      // Ids only. discounting/autosave.js has the reasoning: writing the rest of
      // the response back would undo whatever was typed while it was in flight.
      setFormState((prev) => {
        const rows = mergeSavedRowIds(prev.rows, submittedKeys, body.data?.purchasers_costs)
        return rows === prev.rows ? prev : { ...prev, rows }
      })
      setRowErrors({})
      // Only clean when nothing was typed during the request. Otherwise the
      // queued flush still has work, and the cable handler must go on treating
      // this form as unsaved so an incoming broadcast cannot clobber it.
      if (editSeqRef.current === seqAtStart) dirtyRef.current = false
    } catch (e) {
      setSaveError({ code: 'save_failed', message: t('errors.save_failed', translations) })
    } finally {
      inFlightRef.current = false
      if (rerunRef.current) {
        rerunRef.current = false
        flushRef.current?.()
      }
    }
  }, [readOnly, sendSave, translations, errorMessage, setFormState])

  useEffect(() => { flushRef.current = flushSave }, [flushSave])

  // Everything below goes through flushRef and takes no dependencies. The point
  // is the cleanup: with `[flushSave]` it re-runs whenever that identity changes,
  // and the identity is stable today only because the mount controller renders
  // this component exactly once, so `translations` keeps its object identity.
  // The day the tab is rendered from a React parent that would silently become
  // "clear the debounce and flush on every render" — a save per keystroke, with
  // no symptom. That is the shape OperatingExpensesTab.jsx:328 has.
  const scheduledSave = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushRef.current?.(), DEBOUNCE_DELAY)
  }, [])

  // Leaving the tab is a Turbo navigation, which unmounts the component but
  // leaves the document alive, so a plain fetch started here still lands.
  useEffect(() => () => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushRef.current?.()
  }, [])

  // A real unload — reload, tab close, an external or `data-turbo="false"` link
  // — is the case the cleanup above cannot cover: Stimulus never disconnects,
  // and a plain fetch dies with the document. Since scheduledSave restarts the
  // timer on every keystroke, what is at stake is not 800 ms but everything
  // since the user last paused typing.
  //
  // It deliberately ignores inFlightRef: on unload there is no "later", so a
  // duplicate write of the same rows is the cheaper mistake. The response is
  // unreadable in a torn-down document, so no id merge — the next page load
  // rehydrates from the server.
  useEffect(() => {
    if (readOnly) return undefined

    const onPageHide = () => {
      if (!dirtyRef.current) return
      sendSave(formRef.current, generateRequestId(), true).catch(() => {})
    }

    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [readOnly, sendSave])

  const touch = useCallback(() => {
    // Unreachable while every input is `disabled` under readOnly, but a change
    // event that slipped through would latch dirtyRef true forever and, as a
    // side effect, mute the cable sync below for the rest of the session.
    if (readOnly) return

    dirtyRef.current = true
    editSeqRef.current += 1
    // Errors are keyed by row index, so any edit that can shift indices makes
    // the highlight point at the wrong row. Clear it rather than mislead — and
    // clear the banner with it, or it goes on naming a row that is no longer
    // highlighted until the next flush answers.
    setRowErrors({})
    // Only the row-scoped error goes. `purchasers_cost_not_found` and its
    // siblings say "reload the page", and that state is terminal — dirtyRef
    // stays true, so the cable handler will not replace the dead id either.
    // Clearing it per keystroke would leave the instruction visible only during
    // a pause longer than the debounce.
    setSaveError((prev) => (prev?.code === 'validation_error' ? null : prev))
    scheduledSave()
  }, [readOnly, scheduledSave])

  const setField = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
    touch()
  }, [setFormState, touch])

  const updateRow = useCallback((key, changes) => {
    setFormState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
    }))
    touch()
  }, [setFormState, touch])

  const addRow = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      rows: [...prev.rows, blankDraftRow()]
    }))
    touch()
  }, [setFormState, touch])

  const removeRow = useCallback((key) => {
    setFormState((prev) => ({ ...prev, rows: prev.rows.filter((row) => row.key !== key) }))
    touch()
  }, [setFormState, touch])

  // Switching method clears the value and the VAT flag: the two methods hold
  // different kinds of number, so carrying one across would silently reinterpret
  // "5" from 5% into 5 currency units.
  const changeRowMethod = useCallback((key, inputMethod) => {
    updateRow(key, { inputMethod, amount: '', applyVat: false })
  }, [updateRow])

  const applyBasisPoints = useCallback((bps) => {
    const moved = adjustByBasisPoints(form.discountRatePercent, bps)
    if (moved === null) return
    setField('discountRatePercent', moved)
  }, [form.discountRatePercent, setField])

  const rateIsAdjustable = adjustByBasisPoints(form.discountRatePercent, 0) !== null

  // Live cross-client sync: skip our own echo, and never clobber unsaved edits.
  useEffect(() => {
    if (!valuationId) return undefined
    const subscription = cable.subscriptions.create(
      { channel: 'DiscountingChannel', valuation_id: valuationId },
      {
        received: (data) => {
          if (!data) return
          // Our own echo. A set rather than the last id alone: autosave can have
          // two saves in flight, and the earlier echo carries the payload of the
          // earlier save. The dirty guard below catches it while the second save
          // is still running — but not after it lands and cleans the form, and
          // then that stale echo rehydrates the tab from state the server has
          // already moved past.
          if (ownRequestIdsRef.current.delete(data.request_id)) return
          if (dirtyRef.current || !data.data) return
          // No seed here on purpose — see withSeededRow. An emptied schedule is
          // shown as empty, not answered with a fresh blank row.
          setFormState(toFormState(data.data))
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, setFormState])

  return (
    <div className="discounting-tab">
      {saveError && <div className="alert alert-danger">{saveError.message}</div>}

      <div className="card mb-3">
        <div className="card-header">
          <h5 className="card-title mb-0">{t('discounting.title', translations)}</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="discount-rate">
                {t('discounting.rate_label', translations)}
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                id="discount-rate"
                value={form.discountRatePercent}
                placeholder={t('discounting.rate_placeholder', translations)}
                disabled={readOnly}
                onChange={(e) => {
                  if (isValidDecimalDraft(e.target.value)) setField('discountRatePercent', e.target.value)
                }}
                // Through setField, not a bare setFormState: canonicalising
                // cannot change the parsed value today (the draft regex and
                // formatDecimalInput share DECIMAL_PLACES), but that invariant
                // lives in another file, and with no Save button a blur that
                // did change it would be an unsaved mutation with nothing to
                // catch it. Same shape as the row-amount blur below.
                onBlur={() => {
                  const canonical = formatDecimalInput(parseDecimalInput(form.discountRatePercent))
                  if (canonical !== form.discountRatePercent) setField('discountRatePercent', canonical)
                }}
              />
              <div
                className="btn-group btn-group-sm mt-2 flex-wrap"
                role="group"
                aria-label={t('quick_adjust.aria_label', translations)}
              >
                {BASIS_POINT_STEPS.map(({ key, bps }) => (
                  <button
                    key={key}
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={readOnly || !rateIsAdjustable}
                    onClick={() => applyBasisPoints(bps)}
                  >
                    {t(`quick_adjust.${key}`, translations)}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label" htmlFor="discounting-method">
                {t('discounting.method_label', translations)}
              </label>
              <select
                className="form-select"
                id="discounting-method"
                value={form.discountingMethod}
                disabled={readOnly}
                onChange={(e) => setField('discountingMethod', e.target.value)}
              >
                {DISCOUNTING_METHODS.map((method) => (
                  <option key={method} value={method}>{t(`methods.${method}`, translations)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h5 className="card-title mb-0">{t('purchasers_costs.title', translations)}</h5>
        </div>
        <div className="card-body">
          <p className="text-muted">{t('purchasers_costs.subtitle', translations)}</p>

          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <label className="form-label" htmlFor="vat-rate">
                {t('purchasers_costs.vat_label', translations)}
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                id="vat-rate"
                value={form.vatRatePercent}
                disabled={readOnly}
                onChange={(e) => {
                  if (isValidDecimalDraft(e.target.value)) setField('vatRatePercent', e.target.value)
                }}
                onBlur={() => {
                  const canonical = formatDecimalInput(parseDecimalInput(form.vatRatePercent))
                  if (canonical !== form.vatRatePercent) setField('vatRatePercent', canonical)
                }}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold">{t('purchasers_costs.schedule_label', translations)}</span>
            <button type="button" className="btn btn-primary btn-sm" disabled={readOnly} onClick={addRow}>
              {t('purchasers_costs.add', translations)}
            </button>
          </div>

          {form.rows.length === 0 ? (
            <p className="text-muted mb-0">{t('purchasers_costs.empty', translations)}</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t('purchasers_costs.columns.name', translations)}</th>
                    <th>{t('purchasers_costs.columns.method', translations)}</th>
                    <th>{t('purchasers_costs.columns.value', translations)}</th>
                    <th className="text-center">{t('purchasers_costs.columns.apply_vat', translations)}</th>
                    <th className="text-center">{t('purchasers_costs.columns.delete', translations)}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.rows.map((row) => {
                    const isFixed = row.inputMethod === FIXED_AMOUNT
                    const validate = isFixed ? isValidFixedAmountDraft : isValidDecimalDraft
                    const parse = isFixed ? parseFixedAmountInput : parseDecimalInput
                    const format = isFixed ? formatFixedAmountInput : formatDecimalInput

                    const rowError = rowErrors[row.key]

                    return (
                      <tr
                        key={row.key}
                        className={rowError ? 'table-danger' : undefined}
                        title={rowError ? t('errors.row_invalid', translations) : undefined}
                      >
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.name}
                            placeholder={t('purchasers_costs.name_placeholder', translations)}
                            disabled={readOnly}
                            onChange={(e) => updateRow(row.key, { name: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={row.inputMethod}
                            disabled={readOnly}
                            onChange={(e) => changeRowMethod(row.key, e.target.value)}
                          >
                            {INPUT_METHODS.map((method) => (
                              <option key={method} value={method}>
                                {t(`purchasers_costs.input_methods.${method}`, translations)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control form-control-sm"
                            value={row.amount}
                            placeholder={t(
                              isFixed ? 'purchasers_costs.amount_placeholder' : 'purchasers_costs.percent_placeholder',
                              translations
                            )}
                            disabled={readOnly}
                            onChange={(e) => {
                              if (validate(e.target.value)) updateRow(row.key, { amount: e.target.value })
                            }}
                            onBlur={() => {
                              const canonical = format(parse(row.amount))
                              if (canonical !== row.amount) updateRow(row.key, { amount: canonical })
                            }}
                          />
                        </td>
                        <td className="text-center">
                          {/* VAT applies to percentage rows only: a fixed amount is
                              entered gross, so the checkbox is rendered disabled. */}
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!isFixed && row.applyVat}
                            disabled={readOnly || isFixed}
                            onChange={(e) => updateRow(row.key, { applyVat: e.target.checked })}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={t('purchasers_costs.delete_row', translations)}
                            disabled={readOnly}
                            onClick={() => removeRow(row.key)}
                          >
                            &times;
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
